/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import type { CSSProperties, ReactNode } from "react";
import { cx } from "./cx";

export type VizTone = "good" | "warn" | "bad" | "blue" | "ink" | "purple" | "alert" | "idle";

const TONE_FILL: Record<VizTone, string> = {
  good: "var(--rd-green)",
  warn: "var(--rd-amber)",
  bad: "var(--rd-red)",
  blue: "var(--rd-primary)",
  ink: "var(--rd-text)",
  purple: "var(--rd-purple)",
  alert: "var(--rd-red-alert)",
  idle: "var(--rd-toggle-off)",
};

const pct = (value: number) => `${Math.max(0, Math.min(100, Number(value) || 0))}%`;

/** A labelled 6px bar. The number is always written next to it. */
export function MarginBar({
  label,
  note,
  value,
  percent,
  tone,
}: {
  label: ReactNode;
  note?: ReactNode;
  /** 0–100. */
  percent: number;
  value?: ReactNode;
  tone: VizTone;
}) {
  return (
    <div className="rd-mbar">
      <span className="rd-mbar-label">
        <b>{label}</b>
        {note ? <span>{note}</span> : null}
      </span>
      <span
        className="rd-mbar-track"
        role="meter"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeof label === "string" ? label : undefined}
      >
        <span className="rd-mbar-fill" style={{ width: pct(percent), background: TONE_FILL[tone] }} />
      </span>
      <span className="rd-mbar-value" dir="ltr" style={{ color: TONE_FILL[tone] }}>
        {value ?? `${Math.round(percent)}%`}
      </span>
    </div>
  );
}

export function SplitBar({
  label,
  note,
  parts,
  ariaLabel,
}: {
  label?: ReactNode;
  note?: ReactNode;
  parts: Array<{ percent: number; tone?: VizTone; label?: string }>;
  ariaLabel: string;
}) {
  return (
    <div className="rd-split">
      {label || note ? (
        <div className="rd-viz-head">
          <span>{label}</span>
          <span>{note}</span>
        </div>
      ) : null}
      <div className="rd-split-bar" role="img" aria-label={ariaLabel}>
        {parts.map((part, i) => (
          <span key={i} title={part.label} style={{ width: pct(part.percent), background: TONE_FILL[part.tone ?? "good"] }} />
        ))}
      </div>
    </div>
  );
}

/** A 7-bar sparkline, 32px tall; the highlighted bar (default: last) in blue. */
export function Sparkline({
  values,
  label,
  trend,
  highlight,
  tips,
  ariaLabel,
}: {
  values: number[];
  label?: ReactNode;
  trend?: ReactNode;
  highlight?: number;
  tips?: string[];
  ariaLabel: string;
}) {
  const max = Math.max(1, ...values);
  const hl = highlight ?? values.length - 1;
  return (
    <div className="rd-spark">
      {label || trend ? (
        <div className="rd-viz-head">
          <span>{label}</span>
          <span>{trend}</span>
        </div>
      ) : null}
      <div className="rd-spark-bars" role="img" aria-label={ariaLabel}>
        {values.map((value, i) => (
          <span
            key={i}
            className={cx("rd-spark-bar", i === hl && "rd-today")}
            title={tips ? tips[i] : String(value)}
            style={{ height: `${Math.max(2, Math.round((value / max) * 30))}px` }}
          />
        ))}
      </div>
    </div>
  );
}

export type TimelineItem = {
  id?: string;
  time: ReactNode;
  /** Ring colour; filled = a settled state. */
  tone?: VizTone;
  filled?: boolean;
  label: ReactNode;
  note?: ReactNode;
  tag?: ReactNode;
  amount?: ReactNode;
};

/** History: time | rail | what happened | amount. */
export function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <div className="rd-timeline" role="list">
      {items.map((item, i) => (
        <div key={item.id ?? i} className="rd-tl-row" role="listitem">
          <span className="rd-tl-time">{item.time}</span>
          <div className="rd-tl-rail">
            <span className="rd-tl-line-top" />
            <span className="rd-tl-line-bot" />
            <span
              className={cx("rd-tl-dot", item.filled && "rd-filled")}
              style={{ "--tl-c": TONE_FILL[item.tone ?? "ink"] } as CSSProperties}
            />
          </div>
          <div className="rd-tl-body">
            <span className="rd-tl-head">
              <span className="rd-tl-label">{item.label}</span>
              {item.tag ?? null}
            </span>
            {item.note ? <span className="rd-tl-note">{item.note}</span> : null}
          </div>
          <div className="rd-tl-amt">{item.amount != null ? <b>{item.amount}</b> : null}</div>
        </div>
      ))}
    </div>
  );
}

export function Stepper({
  steps,
}: {
  steps: Array<{ label: ReactNode; date?: ReactNode; state?: "done" | "todo" | "missing"; current?: boolean }>;
}) {
  return (
    <ol
      className="rd-steps"
      style={{ gridTemplateColumns: `repeat(${steps.length},minmax(0,1fr))`, margin: 0, padding: 0, listStyle: "none" }}
    >
      {steps.map((step, i) => (
        <li key={i} className={cx("rd-step", `rd-step-${step.state ?? "todo"}`)} aria-current={step.current ? "step" : undefined}>
          <div className="rd-step-track">
            <span className="rd-step-dot" />
            <span className={cx("rd-step-line", steps[i + 1]?.state === "done" && "rd-done")} />
          </div>
          <span className="rd-step-label">{step.label}</span>
          {step.date ? <span className="rd-step-date">{step.date}</span> : null}
        </li>
      ))}
    </ol>
  );
}
